export interface Option {
    text: string;
    value: string;
}

function toOption(o: HTMLOptionElement): Option {
    return { value: o.getAttribute('value')!, text: o.innerText };
}

export default class SelectModel {
    private readonly value: Set<HTMLOptionElement>;
    private readonly options: HTMLOptionElement[];
    private readonly valueOptionMap: Map<string, HTMLOptionElement> = new Map();
    public readonly multiple: boolean;

    constructor(private readonly selectElement: HTMLSelectElement) {
        this.multiple = selectElement.hasAttribute('multiple');
        this.options = Array.from(selectElement.querySelectorAll('option'))
            .filter(o => o.hasAttribute('value'));
        const selectedOptions = this.options.filter(o => o.hasAttribute('selected'));
        if (!this.multiple) {
            this.value = new Set([selectedOptions[0] || this.options[0]]);
        } else {
            this.value = new Set(selectedOptions);
        }

        this.options.forEach(o => {
            this.valueOptionMap.set(o.getAttribute('value')!, o);
        });

        this.options.forEach(o => o.selected = false);
        this.value.forEach(o => o.selected = true);
    }

    get length (): number {
        return this.options.length;
    }

    getOptions() {
        return this.options.map(toOption);
    }

    getSelectedOptions(): Option[] {
        return this.options.filter(o => this.value.has(o)).map(toOption);
    }

    select(value: string): void {
        const option: HTMLOptionElement | undefined = this.valueOptionMap.get(value);
        if (!option) return;

        if (this.multiple) {
            if (this.value.has(option)) {
                option.selected = false;
                this.value.delete(option);
            } else {
                option.selected = true;
                this.value.add(option);
            }
        } else {
            this.value.forEach(o => o.selected = false);
            this.value.clear();
            this.value.add(option);
            option.selected = true;
        }

        this.emitChange();
    }

    isSelected(value: string): boolean {
        const option = this.valueOptionMap.get(value);
        return !!option && this.value.has(option);
    }

    private emitChange() {
        const event = document.createEvent("HTMLEvents");
        event.initEvent("change", false, true);
        this.selectElement.dispatchEvent(event);
    }
}
